import React, { useContext, useState, useMemo } from 'react';
import { Form } from 'react-final-form';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { useAutoSaveDraft } from '../../commons/draftHooks';
import { AuthContext } from '../../commons/auth/AuthContext';
import { saveInvoiceDraft } from '../invoicingRoutes';

import ConsultantInvoiceBasicDetailsSection from './ConsultantInvoiceBasicDetailsSection';
import ConsultantInvoiceAdditionalChecksSection from './ConsultantInvoiceAdditionalChecksSection';
import ConsultantInvoiceTaxDocumentDigitalSignatureSection from './ConsultantInvoiceTaxDocumentDigitalSignatureSection';
import ConsultantInvoicePaymentSection from './payment-details/ConsultantInvoicePaymentSection';
import {
    isTaxDocumentSupportedForCountry,
    isTaxDocumentMandatoryForCountry,
    getPaymentModeOptionsByCountry,
    getTaxDocumentLabelByCountry,
} from '../../commons/CountryUtils';
import {
    TotalCallAmountCalculateDecorator,
    FormValidator,
} from './InvoiceFormHelpers';
import { convertBooleanToYesNo } from '../../commons/utils';
import { formatDateString } from '../../commons/DateUtils';
import { PageErrorComponent } from '../../commons/PageErrorComponent';
import GenericFormPrivacyPolicySection from '../../commons/privacy-policy/GenericFormPrivacyPolicySection';
import GenericTextAreaInputField from '../../commons/form-fields/GenericTextAreaInputField';
import MultiPageFormNavigationButtons from '../../commons/utils/MultiPageFormNavigationButtons';

const saveDraftData = (
    formValues,
    passedAuthContext,
    passedUrl,
    initialDateOfConsultation
) => {
    if (!passedAuthContext?.auth?.isExpired) {
        saveInvoiceDraft(
            passedUrl,
            {
                values: {
                    ...formValues,
                    // pass the original date of consultation to the draft because the one displayed in the form is the formatted version
                    dateOfConsultation: initialDateOfConsultation,
                },
            },
            passedAuthContext
        );
    }
};

export const InvoiceForm = (props) => {
    const authContext = useContext(AuthContext);

    const {
        submissionError,
        initialValues,
        onSubmit,
        url,
        initialPageNumber = 1,
    } = props;

    const { consultantRegion, consultantCountry } = initialValues;

    // number of pages in the invoice form; two if tax document collection is supported, one otherwise
    const formPagesCount = isTaxDocumentSupportedForCountry(consultantCountry)
        ? 2
        : 1;
    const [currentPage, setCurrentPage] = useState(initialPageNumber);

    const currency = _.get(
        initialValues,
        'consultantInvoiceRateOfCompensation.currency'
    );

    let { dateOfConsultation, repeatConsultant } = initialValues;
    repeatConsultant = convertBooleanToYesNo(repeatConsultant);
    dateOfConsultation = formatDateString(dateOfConsultation);

    // if on the last page, submit the form, otherwise navigate to the next page
    const handleFormSubmit = (values) => {
        if (_.isEqual(currentPage, formPagesCount)) {
            return onSubmit(values);
        }
        setCurrentPage((currPage) => currPage + 1);
        window.scrollTo(0, 0);
        return Promise.resolve({});
    };

    // Memoize the initial form values to avoid creating a new object on every render.
    // This prevents Final Form from resetting unmounted fields (of which it doesn't maintain a dirty state) to their initial state,
    // which occurs when the form receives a new object reference each render.
    const initialFormValues = useMemo(
        () => ({
            ...initialValues,
            paymentMode:
                initialValues?.paymentMode ||
                _.get(
                    _.first(getPaymentModeOptionsByCountry(consultantCountry)),
                    'value'
                ),
            dateOfConsultation,
            repeatConsultant,
            consultantInvoiceChequeDetails: {
                ..._.get(initialValues, 'consultantInvoiceChequeDetails', {}),
                beneficiaryAddress: {
                    ..._.get(
                        initialValues,
                        'consultantInvoiceChequeDetails.beneficiaryAddress',
                        {}
                    ),
                    country: consultantCountry,
                },
            },
            consultantInvoiceWireTransferDetails: {
                ..._.get(
                    initialValues,
                    'consultantInvoiceWireTransferDetails',
                    {}
                ),
                beneficiaryAddress: {
                    ..._.get(
                        initialValues,
                        'consultantInvoiceWireTransferDetails.beneficiaryAddress',
                        {}
                    ),
                    country: consultantCountry,
                },
            },
            consultantRegion,
            consultantCountry,
        }),
        [initialValues]
    );

    return (
        <Form
            keepDirtyOnReinitialize
            onSubmit={handleFormSubmit}
            initialValues={initialFormValues}
            decorators={[TotalCallAmountCalculateDecorator]}
            validate={FormValidator}
            render={({
                handleSubmit,
                errors,
                submitFailed,
                values,
                submitting,
            }) => {
                // Auto-save draft functionality: Save form progress every 10 seconds
                useAutoSaveDraft(
                    (formValues, formErrors, additionalDeps) => {
                        const [lAuthContext, lUrl] = additionalDeps;
                        saveDraftData(
                            formValues,
                            lAuthContext,
                            lUrl,
                            initialValues.dateOfConsultation
                        );
                    },
                    values,
                    undefined,
                    [authContext, url],
                    10_000
                );

                // Manual save draft (for explicit save event)
                const saveDraft = (formValues) => {
                    saveDraftData(
                        formValues,
                        authContext,
                        url,
                        initialValues.dateOfConsultation
                    );
                };

                // Handler to trigger manual save before Adobe Sign redirect
                const handleBeforeAdobeSign = () => {
                    saveDraft(values);
                };

                return (
                    <>
                        {/* Welcome message for the first page */}
                        {currentPage === 1 && (
                            <div className="w-full bg-white text-lg text-center py-2 font-semibold text-customGreen leading-relaxed">
                                Thank you for consulting with D. E. Shaw Group!
                                <br />
                                Please submit your invoice details below for us
                                to process your payment.
                            </div>
                        )}
                        <form
                            onSubmit={handleSubmit}
                            className="mx-auto mt-8 w-full md:w-4/5"
                            autoComplete="off"
                        >
                            {/* Details other than tax document */}
                            {currentPage === 1 && (
                                <>
                                    <ConsultantInvoiceBasicDetailsSection
                                        currency={currency}
                                    />
                                    <ConsultantInvoiceAdditionalChecksSection
                                        consultantCountry={consultantCountry}
                                    />
                                    <ConsultantInvoicePaymentSection
                                        consultantCountry={consultantCountry}
                                    />
                                    {/* Consultant Remarks Field */}
                                    <div>
                                        <hr />
                                        <div className="mt-5">
                                            <GenericTextAreaInputField
                                                name="consultantRemarks"
                                                label={
                                                    <span>
                                                        Remarks{' '}
                                                        <i>(optional)</i>
                                                    </span>
                                                }
                                                type="text"
                                                placeholder={
                                                    'Anything you would like to share with us or keep in mind about the above details...'
                                                }
                                            />
                                        </div>
                                    </div>
                                    <GenericFormPrivacyPolicySection
                                        country={consultantCountry}
                                    />
                                </>
                            )}

                            {/* Tax document section */}
                            {currentPage === 2 && (
                                <>
                                    <ConsultantInvoiceTaxDocumentDigitalSignatureSection
                                        // Pass the required prop to determine if validations should be enforced
                                        required={isTaxDocumentMandatoryForCountry(
                                            consultantCountry,
                                            values
                                        )}
                                        taxDocumentLabel={getTaxDocumentLabelByCountry(
                                            consultantCountry
                                        )}
                                        handleBeforeAdobeSign={
                                            handleBeforeAdobeSign
                                        }
                                    />
                                </>
                            )}

                            {/* Error Display */}
                            {submitFailed && (
                                <PageErrorComponent error={errors} />
                            )}
                            {submissionError && (
                                <PageErrorComponent
                                    error={submissionError}
                                    message={submissionError}
                                />
                            )}

                            {/* Navigation Buttons for Multi-Page Form */}
                            <MultiPageFormNavigationButtons
                                currentPage={currentPage}
                                totalPages={formPagesCount}
                                showPageIndicator={true}
                                onPrev={() => {
                                    setCurrentPage((currPage) => currPage - 1);
                                    window.scrollTo(0, 0);
                                }}
                                disableButtons={submitting}
                            />
                        </form>
                    </>
                );
            }}
        />
    );
};

InvoiceForm.propTypes = {
    initialValues: PropTypes.object,
    onSubmit: PropTypes.func,
    url: PropTypes.string,
    submissionError: PropTypes.string,
    initialPageNumber: PropTypes.number,
};
